import { AfterViewInit, Component } from '@angular/core';
import { BaseComponent } from '../../base/base.component';
import { WorkspaceStore } from '../../../services/stores/workspace.store';
import { BlueprintStore } from '../../../services/stores/blueprint.store';
import { ProjectStore } from '../../../services/stores/projects.store';
import { LabelTreeHelper } from '../../../services/helpers/label-tree.helper';
import { IWorkspaceInstanceModel } from '../../../models/workspace/workspace-instance.model';
import { LABEL_PATH_SEPARATOR } from '../../../modules/catch-core/data/constants/app.constants';

@Component({
  selector: 'app-label-name',
  templateUrl: './label-name.component.html',
  styleUrls: ['./label-name.component.scss'],
  standalone: false,
})
export class LabelNameComponent extends BaseComponent implements AfterViewInit {
  public workspaceTitle: string = '';
  public fullWorkspaceTitle: string = '';
  public nodesNumCount: number = 0;
  public blueprintTooltip: string = '';
  private selectedWorkspace: IWorkspaceInstanceModel | null = null;

  constructor(
    private workspaceStore: WorkspaceStore,
    private blueprintStore: BlueprintStore,
    private projectStore: ProjectStore,
    private labelTreeHelper: LabelTreeHelper,
  ) {
    super();
  }

  ngAfterViewInit(): void {
    if (!this.workspaceStore || !this.workspaceStore.$selectedWorkspaceInstance) {
      return;
    }

    this.subscribers.selectedWorkspace = this.workspaceStore.$selectedWorkspaceInstance.subscribe((res) => {
      if (!res) {
        this.selectedWorkspace = null;
        this.nodesNumCount = 0;
        this.workspaceTitle = '';
        this.fullWorkspaceTitle = '';
        this.blueprintTooltip = '';
        return;
      }

      this.selectedWorkspace = res;
      if (res.bluePrintId) {
        this.blueprintStore.ensureBlueprintLoaded(res.bluePrintId);
      }

      this.nodesNumCount = res.nodeInstances?.length ?? 0;
      this.fullWorkspaceTitle = this.buildLabelPath(res);
      this.workspaceTitle = this.truncateTitle(this.fullWorkspaceTitle);
      this.updateBlueprintTooltip(res);
    });

    this.subscribers.selectedBlueprint = this.blueprintStore.$selectedBluePrint.subscribe((blueprint) => {
      if (!this.selectedWorkspace?.bluePrintId || !blueprint || blueprint.id !== this.selectedWorkspace.bluePrintId) {
        return;
      }

      this.updateBlueprintTooltip(this.selectedWorkspace);
    });

    this.subscribers.allBlueprints = this.blueprintStore.$allBluePrints.subscribe(() => {
      if (!this.selectedWorkspace?.bluePrintId) {
        return;
      }

      this.updateBlueprintTooltip(this.selectedWorkspace);
    });
  }

  // Falls back to the label's own name (rather than an empty header) if it can't be found in the
  // current project's tree - e.g. an archived label, already pruned from treeNodes, or the tree
  // simply hasn't loaded yet.
  private buildLabelPath(workspace: IWorkspaceInstanceModel): string {
    if (!workspace) {
      return '';
    }

    const treeNodes = this.projectStore?.selectedProject?.treeNodes;
    const path = this.labelTreeHelper.getNodesTreePathById(treeNodes ?? [], workspace.id, LABEL_PATH_SEPARATOR);
    return path || workspace.name || '';
  }

  private truncateTitle(title: string, maxLength: number = 100): string {
    if (!title) return '';
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  }

  public isTitleTruncated(): boolean {
    return this.fullWorkspaceTitle.length > 100;
  }

  public onBlueprintInfoClick(event: MouseEvent, popover: any): void {
    if (!this.blueprintTooltip) {
      return;
    }

    event.stopPropagation();
    popover.toggle(event);
  }

  private updateBlueprintTooltip(workspace: IWorkspaceInstanceModel | null): void {
    const blueprint = this.blueprintStore.getBlueprintByIdFromCache(workspace?.bluePrintId);
    this.blueprintTooltip = blueprint?.name ? `Template: ${blueprint.name}` : '';
  }
}
